import React, { useEffect, useState, useRef } from "react";
import ReactModal from "react-modal";
import "./ems-boot-modal.scss";
import ContactInformation from "./v2/ContactInformation";
import PricingModel from "./v2/PricingModel";
import { FaTimes } from "react-icons/fa";
import { IoMdCheckmark } from "react-icons/io";
import {
    createEMSBootApp,
    getEquipments,
    GetPricingEquipmentTemplateById,
    getTemplateForApplication,
    getOrganizationPricingDefaults
} from "../../../api";
import config from "../../../config";
import LineItems from "./v2/LineItems";
import { Form } from "react-bootstrap";
import _ from "lodash";
import eventEmitter from "./eventEmitter";
import SlidingButton from "./v2/input-elements/SlidingButton";
import BatchTime from "./v2/input-elements/BatchTime";
import { EquipmentContext } from "./useContext/context";
import { Stack } from "@mui/material";
import pricingValidation from "./pricing-validation";
import { EMS_OTHER_DEFAULT_FEE, PAYCHEX_OTHER_DEFAULT_FEE } from "../../../constants";
import MerchantReferral from "./v2/MerchantReferral";
import AddedItemCount from "./v2/AddedItemCount";
import CancellationFee from "./v2/CancellationFee";
import CountrySelection from "./v2/input-elements/CountrySelection";
import PaychexOptions from "./v2/PaychexOptions";
import Select from 'react-select'

const pricing_model_options = [
    { label: "Dual Pricing", type: "dual_pricing" },
    { label: "Surcharge", type: "surcharge" },
    { label: "Flat Rate", type: "flat_rate" },
    { label: "Tiered Rate", type: "tiered" },
    { label: "IC+", type: "ic_plus" },
];

const MAIN_URL = config.paymentcloud.APPLICATION_URL;
const PAYCHEX_ORG_ID = config.paymentcloud.PAYCHEX_ORG_ID;
const EMS_ORG_ID = config.paymentcloud.EMS_ORG_ID;

const flat_rate_validation = pricingValidation.pricingValidation.flat_rate;
const ic_plus_validation = pricingValidation.pricingValidation.ic_plus;
const surcharge_validation = pricingValidation.pricingValidation.surcharge;
const tiered_validation = pricingValidation.pricingValidation.tiered;
const ach_validation = pricingValidation.pricingValidation.ach;

// Helper function to fetch pricing defaults with fallback
const fetchPricingDefaults = async (organizationId) => {
    try {
        // Get all organization-specific defaults
        const orgDefaults = await getOrganizationPricingDefaults(organizationId);
        
        if (orgDefaults && orgDefaults.data) {
            return orgDefaults.data;
        }
        // Fallback to validation defaults for all models
        return {
            dual_pricing: pricingValidation.pricingValidation.dual_pricing,
            surcharge: pricingValidation.pricingValidation.surcharge,
            flat_rate: pricingValidation.pricingValidation.flat_rate,
            tiered: pricingValidation.pricingValidation.tiered,
            ic_plus: pricingValidation.pricingValidation.ic_plus,
            ach: pricingValidation.pricingValidation.ach,
        };
    } catch (error) {
        console.error('Error fetching pricing defaults:', error);
    }

    // Fallback to validation defaults for all models
    return {
        dual_pricing: pricingValidation.pricingValidation.dual_pricing,
        surcharge: pricingValidation.pricingValidation.surcharge,
        flat_rate: pricingValidation.pricingValidation.flat_rate,
        tiered: pricingValidation.pricingValidation.tiered,
        ic_plus: pricingValidation.pricingValidation.ic_plus,
        ach: pricingValidation.pricingValidation.ach,
    }
};

// Helper function to convert defaults to form data format for all models
const convertDefaultsToFormData = (defaults) => {
    const formData = {};
    
    // Process all pricing models
    Object.entries(defaults).forEach(([modelKey, modelDefaults]) => {
        Object.entries(modelDefaults).forEach(([key, value]) => {
            if (value && typeof value === 'object' && value.default !== undefined) {
                // Handle numeric values with proper formatting
                if (typeof value.default === 'number') {
                    formData[key] = value.default.toFixed(2);
                } else {
                    formData[key] = value.default;
                }
            }
        });
    });
    
    return formData;
};

function EMSBootModal(props) {
    const [isOpen, setIsOpen] = useState(true);
    const [org_id, setOrgId] = useState(props?.user?.organization_id);
    const [pricingDefaults, setPricingDefaults] = useState({});
    
    const dual_pricing_validation =
        org_id == config.paymentcloud.PAYCHEX_ORG_ID
            ? pricingValidation.pricingValidation.paychex_dual_pricing
            : pricingValidation.pricingValidation.dual_pricing;
    let other_default_fee = {};
    if (org_id == config.paymentcloud.PAYCHEX_ORG_ID) {
        other_default_fee = PAYCHEX_OTHER_DEFAULT_FEE;
    } else {
        other_default_fee = EMS_OTHER_DEFAULT_FEE;
    }
    
    // Initialize form_data with basic structure, defaults will be loaded dynamically
    const [form_data, setFormData] = useState({
        dba_name: "",
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        dba_street: "",
        dba_city: "",
        dba_state: "",
        dba_zip: "",
        selected_template: "",
        pricing_model: "dual_pricing",
        dual_pricing_rate: dual_pricing_validation.dual_pricing_rate.default,
        dual_pricing_per_transaction: dual_pricing_validation.dual_pricing_per_transaction.default.toFixed(2),
        dual_pricing_monthly_fee:
            dual_pricing_validation.dual_pricing_monthly_fee.default.toFixed(2),
        dual_pricing_pin_debit_network_fee:
            dual_pricing_validation.dual_pricing_pin_debit_network_fee.default,
        dual_pricing_pin_debit_rate:
            dual_pricing_validation.dual_pricing_pin_debit_rate.default.toFixed(
                2,
            ),
        dual_pricing_pin_debit_per_transaction:
            dual_pricing_validation.dual_pricing_pin_debit_per_transaction.default.toFixed(
                2,
            ),

        surcharge_rate: surcharge_validation.surcharge_rate.default.toFixed(2),
        surcharge_per_transaction:
            surcharge_validation.surcharge_per_transaction.default.toFixed(2),
        surcharge_monthly_fee:
            surcharge_validation.surcharge_monthly_fee.default.toFixed(2),
        surcharge_qual_debit_rate:
            surcharge_validation.surcharge_qual_debit_rate.default.toFixed(2),
        surcharge_qual_debit_per_transaction:
            surcharge_validation.surcharge_qual_debit_per_transaction.default.toFixed(
                2,
            ),
        surcharge_mid_qual_debit_rate:
            surcharge_validation.surcharge_mid_qual_debit_rate.default.toFixed(
                2,
            ),
        surcharge_mid_qual_debit_per_transaction:
            surcharge_validation.surcharge_mid_qual_debit_per_transaction.default.toFixed(
                2,
            ),
        surcharge_non_qual_debit_rate:
            surcharge_validation.surcharge_non_qual_debit_rate.default.toFixed(
                2,
            ),
        surcharge_non_qual_debit_per_transaction:
            surcharge_validation.surcharge_non_qual_debit_per_transaction.default.toFixed(
                2,
            ),
        surcharge_pin_debit_rate:
            surcharge_validation.surcharge_pin_debit_rate.default.toFixed(2),
        surcharge_pin_debit_per_transaction:
            surcharge_validation.surcharge_pin_debit_per_transaction.default.toFixed(
                2,
            ),
        surcharge_signature_debit_rate:
            surcharge_validation.surcharge_signature_debit_rate.default.toFixed(
                2,
            ),
        surcharge_signature_debit_per_transaction:
            surcharge_validation.surcharge_signature_debit_per_transaction.default.toFixed(
                2,
            ),
        surcharge_pin_debit_network_fee:
            surcharge_validation.surcharge_pin_debit_network_fee.default,

        flat_rate: flat_rate_validation.flat_rate.default.toFixed(2),
        flat_rate_transaction:
            flat_rate_validation.flat_rate_transaction.default.toFixed(2),
        flat_rate_amex: flat_rate_validation.flat_rate_amex.default.toFixed(2),
        flat_rate_amex_transaction:
            flat_rate_validation.flat_rate_amex_transaction.default.toFixed(2),
        flat_rate_pin_debit_network_fee:
            flat_rate_validation.flat_rate_pin_debit_network_fee.default,
        flat_rate_pin_debit_rate:
            flat_rate_validation.flat_rate_pin_debit_rate.default.toFixed(2),
        flat_rate_pin_debit_per_transaction:
            flat_rate_validation.flat_rate_pin_debit_per_transaction.default.toFixed(
                2,
            ),

        tiered_qual_rate: tiered_validation.tiered_qual_rate.default.toFixed(2),
        tiered_qual_transaction:
            tiered_validation.tiered_qual_transaction.default.toFixed(2),
        tiered_qual_debit_rate:
            tiered_validation.tiered_qual_debit_rate.default.toFixed(2),
        tiered_qual_debit_per_transaction:
            tiered_validation.tiered_qual_debit_per_transaction.default.toFixed(
                2,
            ),

        tiered_mid_qual_rate:
            tiered_validation.tiered_mid_qual_rate.default.toFixed(2),
        tiered_mid_qual_transaction:
            tiered_validation.tiered_mid_qual_transaction.default.toFixed(2),
        tiered_mid_qual_debit_rate:
            tiered_validation.tiered_mid_qual_debit_rate.default.toFixed(2),
        tiered_mid_qual_debit_per_transaction:
            tiered_validation.tiered_mid_qual_debit_per_transaction.default.toFixed(
                2,
            ),

        tiered_non_qual_rate:
            tiered_validation.tiered_non_qual_rate.default.toFixed(2),
        tiered_non_qual_transaction:
            tiered_validation.tiered_non_qual_transaction.default.toFixed(2),
        tiered_non_qual_debit_rate:
            tiered_validation.tiered_non_qual_debit_rate.default.toFixed(2),
        tiered_non_qual_debit_per_transaction:
            tiered_validation.tiered_non_qual_debit_per_transaction.default.toFixed(
                2,
            ),

        tiered_amex_structure: "flat_rate", // tiered, flat_rate, ic_plus

        amex_tier_one_rate:
            tiered_validation.amex_tier_one_rate.default.toFixed(2),
        amex_tier_one_fee:
            tiered_validation.amex_tier_one_fee.default.toFixed(2),
        amex_tier_two_rate:
            tiered_validation.amex_tier_two_rate.default.toFixed(2),
        amex_tier_two_fee:
            tiered_validation.amex_tier_two_fee.default.toFixed(2),
        amex_tier_three_rate:
            tiered_validation.amex_tier_three_rate.default.toFixed(2),
        amex_tier_three_fee:
            tiered_validation.amex_tier_three_fee.default.toFixed(2),
        amex_tier_four_rate:
            tiered_validation.amex_tier_four_rate.default.toFixed(2),
        amex_tier_four_fee:
            tiered_validation.amex_tier_four_fee.default.toFixed(2),

        tiered_amex_flat_rate:
            tiered_validation.tiered_amex_flat_rate.default.toFixed(2),
        tiered_amex_flat_transaction:
            tiered_validation.tiered_amex_flat_transaction.default.toFixed(2),

        tiered_amex_ic_plus_rate:
            tiered_validation.tiered_amex_ic_plus_rate.default.toFixed(2),
        tiered_amex_ic_plus_transaction:
            tiered_validation.tiered_amex_ic_plus_transaction.default.toFixed(
                2,
            ),

        tiered_pin_debit_network_fee:
            tiered_validation.tiered_pin_debit_network_fee.default,
        tiered_pin_debit_rate:
            tiered_validation.tiered_pin_debit_rate.default.toFixed(2),
        tiered_pin_debit_per_transaction:
            tiered_validation.tiered_pin_debit_per_transaction.default.toFixed(
                2,
            ),

        ic_plus_rate: ic_plus_validation.ic_plus_rate.default.toFixed(2),
        ic_plus_transaction_fee:
            ic_plus_validation.ic_plus_transaction_fee.default.toFixed(2),
        ic_plus_amex_rate:
            ic_plus_validation.ic_plus_amex_rate.default.toFixed(2),
        ic_plus_amex_transaction_fee:
            ic_plus_validation.ic_plus_amex_transaction_fee.default.toFixed(2),
        ic_plus_pin_debit_network_fee:
            ic_plus_validation.ic_plus_pin_debit_network_fee.default,
        ic_plus_pin_debit_rate:
            ic_plus_validation.ic_plus_pin_debit_rate.default.toFixed(2),
        ic_plus_pin_debit_per_transaction:
            ic_plus_validation.ic_plus_pin_debit_per_transaction.default.toFixed(
                2,
            ),
        monthly_fee: "49.00",
        other_gateway: "",
        pricing_approved_by_rate_desk: "no",
        equipments: {},
        var_only: {},
        ...other_default_fee,
        // merchant referral
        is_merchant_referral: "no",
        referrer_name: "",
        referrer_mid: "",
        is_tm: "yes",
        telemarketer_name: "",
        cold_call: "no",
        tm_lead: "no",
        //EBT
        ebt: "no",
        fnf_number: "",
        country: "en",
        var_sheet_request: "yes",
        who_to_deploy_equipment: "agent",
        who_to_pay_for_equipment: "agent",
        who_to_deploy_gateway: "agent",
        reprogram_equipment: true,
        reprogram_equipment_name: "",
        postal_code: "",
    });

    const [reimbursementMaximum, setReimbursementMaximum] = useState();
    const [coverCancellation, setCoverCancellation] = useState("no");
    const [expedited_funding, setExpeditedFunding] = useState("yes");
    const [billing_sequence, setBillingSequence] = useState("daily");
    const [batch_time, setBatchTime] = useState("06:00 PM");
    const [equipment_address, setEquipmentAddress] = useState("");
    const [invoice_address, setInvoiceAddress] = useState("");
    const [special_instructions, setSpecialInstruction] = useState("");
    const [uw_notes, setUwNotes] = useState("");
    const [errors, setErrors] = useState({});
    const [allEquipments, setAllEquipments] = useState({});
    const [isDeviceExpanded, setIsDeviceExpanded] = useState(false);
    const [categories_selected, setCategoriesSelected] = useState([]);
    const [tpp_equipments_selected, setTppEquipmentsSelected] = useState([]);
    const [equipment_count, setEquipmentCount] = useState(0);
    const [all_equipment_list, setAllEquipmentList] = useState([]);
    const [all_templates, setAllTemplates] = useState([]);
    const [templateOptions, setTemplateOptions] = useState([]);
    const [isEditable, setIsEditable] = useState(true);
    const [template_id, setTemplateId] = useState(null);

    useEffect(() => {
        getEquipments()
            .then((res) => {
                const result = res.data.equipments.reduce((acc, current) => {
                    acc[current.id] = current.equipment_name;
                    return acc;
                }, {});
                setAllEquipments(result);
                setAllEquipmentList(res.data.equipments);
            })
            .catch((err) => {
                console.log(err);
            });


          getTemplateForApplication()
            .then((res) => {
              if(res.status){
                setAllTemplates(res.data);
                setTemplateOptions(res.data.map((template) => ({
                  label: template.name,
                  value: template.id,
                })));
              }
            })
    }, []);

    // Load pricing defaults for the organization
    useEffect(() => {
        const loadPricingDefaults = async () => {
            if (org_id) {
                try {
                    const defaults = await fetchPricingDefaults(org_id);
                    setPricingDefaults(defaults);
                    
                    // Apply all defaults to form data
                    const formDefaults = convertDefaultsToFormData(defaults);
                    setFormData(prevState => ({
                        ...prevState,
                        ...formDefaults
                    }));
                } catch (error) {
                    console.error('Error loading pricing defaults:', error);
                }
            }
        };

        loadPricingDefaults();
    }, [org_id]);

    // Apply template data when template changes
    useEffect(() => {
        if(Object.entries(pricing_template_data).length > 0){
          setFormData({
            ...form_data,
            ebt: pricing_template_data?.ebt || "no",
            is_tm: pricing_template_data?.is_tm || "yes",        
            avs_fee: pricing_template_data?.avs_fee || "0.10",
            country: pricing_template_data?.country || "en",
            etf_fee: pricing_template_data?.etf_fee || "595.00",
            tm_lead: pricing_template_data?.tm_lead || "no",
            var_only: pricing_template_data?.var_only || null,
            batch_fee: pricing_template_data?.batch_fee || "0.30",
            cold_call: pricing_template_data?.cold_call || "no",
            flat_rate: pricing_template_data?.flat_rate || "3.00",
            setup_fee: pricing_template_data?.setup_fee || "125.00",
            voice_aru: pricing_template_data?.voice_aru || "0.60",
            batch_time: pricing_template_data?.batch_time || "06:00 PM", 
            fnf_number: pricing_template_data?.fnf_number || null,
            data_breach: pricing_template_data?.data_breach || "15.00",
            monthly_fee: pricing_template_data?.monthly_fee || "49.00",
            postal_code: pricing_template_data?.postal_code || null,
            selectedOrg: pricing_template_data?.selectedOrg || null,
            ic_plus_rate: pricing_template_data?.ic_plus_rate || "0.15",
            referrer_mid: pricing_template_data?.referrer_mid || null,
            selectedUser: pricing_template_data?.selectedUser || null,        
            other_gateway: pricing_template_data?.other_gateway || null,
            tiered_amex_structure: pricing_template_data?.pricing_model || "ic_plus",
            pricing_model: pricing_template_data?.qualifiedtype || "dual_pricing",
            referrer_name: pricing_template_data?.referrer_name || null,
            chargeback_fee: pricing_template_data?.chargeback_fee || "45.00",
            flat_rate_amex: pricing_template_data?.flat_rate_amex || "3.00",
            surcharge_rate: pricing_template_data?.surcharge_rate || "3.00",
            invoice_address: pricing_template_data?.invoice_address || "home_address",
            monthly_minimum: pricing_template_data?.monthly_minimum || "25.00",
            pre_dispute_fee: pricing_template_data?.pre_dispute_fee || "45.00",
            auth_transaction: pricing_template_data?.auth_transaction || "0.25",
            billing_sequence: pricing_template_data?.billing_sequence || "daily",
            ebt_transactions: pricing_template_data?.ebt_transactions || null,
            monthly_cellular: pricing_template_data?.monthly_cellular || "25.00",
            tiered_qual_rate: pricing_template_data?.tiered_qual_rate || "1.75",
            amex_tier_one_fee: pricing_template_data?.amex_tier_one_fee || "0.20",
            amex_tier_two_fee: pricing_template_data?.amex_tier_two_fee || "0.20",
            dual_pricing_rate: pricing_template_data?.dual_pricing_rate || 4,
            equipment_address: pricing_template_data?.equipment_address || "legal_address",
            expedited_funding: pricing_template_data?.expedited_funding || "yes",
            ic_plus_amex_rate: pricing_template_data?.ic_plus_amex_rate || "0.15",
            representment_fee: pricing_template_data?.representment_fee || "45.00",
            returned_ach_item: pricing_template_data?.returned_ach_item || "25.00",
            selected_template: pricing_template_data?.selected_template || null,
            telemarketer_name: pricing_template_data?.telemarketer_name || null,
            var_sheet_request: pricing_template_data?.var_sheet_request || "yes",
            amex_tier_four_fee: pricing_template_data?.amex_tier_four_fee || "0.20",
            amex_tier_one_rate: pricing_template_data?.amex_tier_one_rate || "3.50",
            amex_tier_two_rate: pricing_template_data?.amex_tier_two_rate || "3.50",
            cover_cancellation: pricing_template_data?.cover_cancellation || "no",
            monthly_access_fee: pricing_template_data?.monthly_access_fee || "15.00",
            amex_tier_four_rate: pricing_template_data?.amex_tier_four_rate || "3.50",
            amex_tier_three_fee: pricing_template_data?.amex_tier_three_fee || "0.20",
            reprogram_equipment: pricing_template_data?.reprogram_equipment || true,
            amex_tier_three_rate: pricing_template_data?.amex_tier_three_rate || "3.50",
            cellular_transaction: pricing_template_data?.cellular_transaction || "0.10",
            is_merchant_referral: pricing_template_data?.is_merchant_referral || "no",
            semi_annual_tech_fee: pricing_template_data?.semi_annual_tech_fee || "75.00",
            special_instructions: pricing_template_data?.special_instructions || "",
            tiered_mid_qual_rate: pricing_template_data?.tiered_mid_qual_rate || "2.29",
            tiered_non_qual_rate: pricing_template_data?.tiered_non_qual_rate || "2.99",
            flat_rate_transaction: pricing_template_data?.flat_rate_transaction || "0.15",
            surcharge_monthly_fee: pricing_template_data?.surcharge_monthly_fee || "49.00",
            tiered_amex_flat_rate: pricing_template_data?.tiered_amex_flat_rate || "3.50",
            tiered_pin_debit_rate: pricing_template_data?.tiered_pin_debit_rate || "0.00",
            who_to_deploy_gateway: pricing_template_data?.who_to_deploy_gateway || "agent",
            ic_plus_pin_debit_rate: pricing_template_data?.ic_plus_pin_debit_rate || "0.00",
            tiered_qual_debit_rate: pricing_template_data?.tiered_qual_debit_rate || "1.50",
            ic_plus_transaction_fee: pricing_template_data?.ic_plus_transaction_fee || "0.15",
            tiered_qual_transaction: pricing_template_data?.tiered_qual_transaction || "0.15",
            who_to_deploy_equipment: pricing_template_data?.who_to_deploy_equipment || "agent",
            dual_pricing_monthly_fee: pricing_template_data?.dual_pricing_monthly_fee || "49.00",
            flat_rate_pin_debit_rate: pricing_template_data?.flat_rate_pin_debit_rate || "2.00",
            reprogram_equipment_name: pricing_template_data?.reprogram_equipment_name || null,
            surcharge_pin_debit_rate: pricing_template_data?.surcharge_pin_debit_rate || "0.00",
            tiered_amex_ic_plus_rate: pricing_template_data?.tiered_amex_ic_plus_rate || "3.50",
            who_to_pay_for_equipment: pricing_template_data?.who_to_pay_for_equipment || "agent",
            surcharge_per_transaction: pricing_template_data?.surcharge_per_transaction || "0.15",
            surcharge_qual_debit_rate: pricing_template_data?.surcharge_qual_debit_rate || "1.50",
            flat_rate_amex_transaction: pricing_template_data?.flat_rate_amex_transaction || "0.15",
            tiered_mid_qual_debit_rate: pricing_template_data?.tiered_mid_qual_debit_rate || "1.99",
            tiered_non_qual_debit_rate: pricing_template_data?.tiered_non_qual_debit_rate || "2.49",
            dual_pricing_pin_debit_rate: pricing_template_data?.dual_pricing_pin_debit_rate || "0.00",
            tiered_mid_qual_transaction: pricing_template_data?.tiered_mid_qual_transaction || "0.15",
            tiered_non_qual_transaction: pricing_template_data?.tiered_non_qual_transaction || "0.15",
            ic_plus_amex_transaction_fee: pricing_template_data?.ic_plus_amex_transaction_fee || "0.15",
            tiered_amex_flat_transaction: pricing_template_data?.tiered_amex_flat_transaction || "0.20",
            tiered_pin_debit_network_fee: pricing_template_data?.tiered_pin_debit_network_fee || "yes",
            ic_plus_pin_debit_network_fee: pricing_template_data?.ic_plus_pin_debit_network_fee || "yes",
            pricing_approved_by_rate_desk: pricing_template_data?.pricing_approved_by_rate_desk || "no",
            surcharge_mid_qual_debit_rate: pricing_template_data?.surcharge_mid_qual_debit_rate || "1.99",
            surcharge_non_qual_debit_rate: pricing_template_data?.surcharge_non_qual_debit_rate || "2.49",
            surcharge_signature_debit_rate: pricing_template_data?.surcharge_signature_debit_rate || "3.00",
            flat_rate_pin_debit_network_fee: pricing_template_data?.flat_rate_pin_debit_network_fee || "yes",
            surcharge_pin_debit_network_fee: pricing_template_data?.surcharge_pin_debit_network_fee || "yes",
            tiered_amex_ic_plus_transaction: pricing_template_data?.tiered_amex_ic_plus_transaction || "0.20",
            tiered_pin_debit_per_transaction: pricing_template_data?.tiered_pin_debit_per_transaction || "0.00",
            ic_plus_pin_debit_per_transaction: pricing_template_data?.ic_plus_pin_debit_per_transaction || "0.00",
            tiered_qual_debit_per_transaction: pricing_template_data?.tiered_qual_debit_per_transaction || "0.15",
            dual_pricing_pin_debit_network_fee: pricing_template_data?.dual_pricing_pin_debit_network_fee || "yes",
            flat_rate_pin_debit_per_transaction: pricing_template_data?.flat_rate_pin_debit_per_transaction || "0.15",
            surcharge_pin_debit_per_transaction: pricing_template_data?.surcharge_pin_debit_per_transaction || "0.00",
            surcharge_qual_debit_per_transaction: pricing_template_data?.surcharge_qual_debit_per_transaction || "0.15",
            tiered_mid_qual_debit_per_transaction: pricing_template_data?.tiered_mid_qual_debit_per_transaction || "0.15",
            tiered_non_qual_debit_per_transaction: pricing_template_data?.tiered_non_qual_debit_per_transaction || "0.15",
            dual_pricing_pin_debit_per_transaction: pricing_template_data?.dual_pricing_pin_debit_per_transaction || "0.00",
            surcharge_mid_qual_debit_per_transaction: pricing_template_data?.surcharge_mid_qual_debit_per_transaction || "0.15",
            surcharge_non_qual_debit_per_transaction: pricing_template_data?.surcharge_non_qual_debit_per_transaction || "0.15",
            surcharge_signature_debit_per_transaction: pricing_template_data?.surcharge_signature_debit_per_transaction || "0.15",     
            equipments: pricing_template_data.equipments,
            //ACH
            ach_by_web: pricing_template_data?.ach_by_web || "no",
            ach_by_phone: pricing_template_data?.ach_by_phone || "no",
            ach_by_preauth: pricing_template_data?.ach_by_preauth || "no",
            ach_credit_transaction: pricing_template_data?.ach_credit_transaction || "no",
            ach_discount_rate: pricing_template_data?.ach_discount_rate || "",
            ach_transaction_fee_per_item:
            pricing_template_data?.ach_transaction_fee_per_item ||
            ach_validation.ach_transaction_fee_per_item.default,
            ach_statement_fee: pricing_template_data?.ach_statement_fee ||
            ach_validation.ach_statement_fee.default,
            ach_monthly_minimum: pricing_template_data?.ach_monthly_minimum ||
            ach_validation.ach_monthly_minimum.default,
            ach_return_fee: pricing_template_data?.ach_return_fee ||
            ach_validation.ach_return_fee.default,
            ach_noc_fee: pricing_template_data?.ach_noc_fee ||
            ach_validation.ach_noc_fee.default,
            ach_unauthorized_return_fee: pricing_template_data?.ach_unauthorized_return_fee ||
            ach_validation.ach_unauthorized_return_fee.default,
            ach_same_day_fee: pricing_template_data?.ach_same_day_fee || "",
            ach_bank_validation_fee: pricing_template_data?.ach_bank_validation_fee || "",
            add_ach: pricing_template_data?.add_ach || "no",
          });
  
          setInvoiceAddress(pricing_template_data?.invoice_address || "home_address");
          setEquipmentAddress(pricing_template_data?.equipment_address || "legal_address");
          setSpecialInstruction(pricing_template_data?.special_instructions || "");
          setUwNotes(pricing_template_data?.uw_notes || "");
          setBatchTime(pricing_template_data?.batch_time || "06:00 PM");
          setExpeditedFunding(pricing_template_data?.expedited_funding || "yes");
          setBillingSequence(pricing_template_data?.billing_sequence || "daily");
          setCoverCancellation(pricing_template_data?.cover_cancellation || "no");
          setReimbursementMaximum(pricing_template_data?.reimbursement_maximum || "0.00");
        }
      }, [template_id]);

    const setFormDataByKeyValue = (key, value) => {
        if (key == "pricing_model") {
            if (["dual_pricing", "surcharge"].includes(value)) {
                setBillingSequence("daily");
            }
        }
        
        if(key == "equipments" && Object.keys(value).length == 0){
          return;
        }
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }));
        removeError(key);
    };

    const setVarOnlyData = (data) => {
        setFormData((prevState) => ({
            ...prevState,
            var_only: data,
        }));
        removeError(['equipments', 'equipment_address', 'invoice_address', 'var_only', 'var_only.type', 'var_only.model', 'var_only.serial']);
    };

    const removeError = (keys) => {
        setErrors((prevErrors) => {
            const keysToRemove = Array.isArray(keys) ? keys : [keys];
            const newErrors = { ...prevErrors };
            
            keysToRemove.forEach((key) => {
                delete newErrors[key];
            });
    
            return newErrors;
        });
    };

    const setAddressByGoogleAPI = (address) => {
        setFormData((prevState) => ({
            ...prevState,
            ...address,
        }));

        setTimeout(() => {
            setErrors((prevErrors) => {
                const { dba_street, dba_city, dba_zip, dba_state, ...rest } =
                    prevErrors;
                return rest;
            });
        }, 500);
    };

    const skipCheck = (field) => {
        if (field == "dual_pricing_pin_debit_network_fee") return false;
        if (field == "flat_rate_pin_debit_network_fee") return false;
        if (field == "tiered_pin_debit_network_fee") return false;
        if (field == "ic_plus_pin_debit_network_fee") return false;
        if (field == "surcharge_pin_debit_network_fee") return false;

        if (form_data.tiered_amex_structure == "tiered") {
            if (field == "tiered_amex_flat_rate") return false;
            if (field == "tiered_amex_flat_transaction") return false;
            if (field == "tiered_amex_ic_plus_rate") return false;
            if (field == "tiered_amex_ic_plus_transaction") return false;
        }

        if (form_data.tiered_amex_structure == "flat_rate") {
            if (field == "tiered_amex_ic_plus_rate") return false;
            if (field == "tiered_amex_ic_plus_transaction") return false;

            if (field == "amex_tier_one_rate") return false;
            if (field == "amex_tier_one_fee") return false;
            if (field == "amex_tier_two_rate") return false;
            if (field == "amex_tier_two_fee") return false;
            if (field == "amex_tier_three_rate") return false;
            if (field == "amex_tier_three_fee") return false;
            if (field == "amex_tier_four_rate") return false;
            if (field == "amex_tier_four_fee") return false;
        }

        if (form_data.tiered_amex_structure == "ic_plus") {
            if (field == "tiered_amex_flat_rate") return false;
            if (field == "tiered_amex_flat_transaction") return false;

            if (field == "amex_tier_one_rate") return false;
            if (field == "amex_tier_one_fee") return false;
            if (field == "amex_tier_two_rate") return false;
            if (field == "amex_tier_two_fee") return false;
            if (field == "amex_tier_three_rate") return false;
            if (field == "amex_tier_three_fee") return false;
            if (field == "amex_tier_four_rate") return false;
            if (field == "amex_tier_four_fee") return false;
        }

        if (
            form_data.pricing_model == "surcharge" &&
            form_data.tiered_amex_structure == "ic_plus"
        ) {
            if (field == "surcharge_qual_debit_rate") return false;
            if (field == "surcharge_qual_debit_per_transaction") return false;
            if (field == "surcharge_mid_qual_debit_rate") return false;
            if (field == "surcharge_mid_qual_debit_per_transaction")
                return false;
            if (field == "surcharge_non_qual_debit_rate") return false;
            if (field == "surcharge_non_qual_debit_per_transaction")
                return false;
        }

        if (
            form_data.pricing_model == "surcharge" &&
            form_data.tiered_amex_structure == "tiered"
        ) {
            if (field == "surcharge_signature_debit_rate") return false;
            if (field == "surcharge_signature_debit_per_transaction")
                return false;
        }

        return true;
    };

    const checkPricingValidation = () => {
        let validation_errors = {};
        let validations = {};
        if (form_data.pricing_model == "dual_pricing")
            validations = dual_pricing_validation;
        if (form_data.pricing_model == "surcharge")
            validations = surcharge_validation;
        if (form_data.pricing_model == "flat_rate")
            validations = flat_rate_validation;
        if (form_data.pricing_model == "tiered")
            validations = tiered_validation;
        if (form_data.pricing_model == "ic_plus")
            validations = ic_plus_validation;

        Object.entries(validations).forEach(([field, validation]) => {
            let value = parseFloat(form_data[field]);
            if (skipCheck(field)) {
            }
        });
        setErrors(validation_errors);
        return Object.entries(validation_errors).length > 0 ? true : false;
    };

    const submitForm = () => {
        if (checkPricingValidation()) {
            return;
        }

        let data = {
            ...form_data,
            reimbursement_maximum: reimbursementMaximum,
            cover_cancellation: coverCancellation,
            expedited_funding,
            billing_sequence,
            batch_time,
            equipment_address,
            invoice_address,
            special_instructions,
            uw_notes,
            submission_type: "create_app",
            mcc_id: pricing_template_data?.mcc_id?.value || null,
            ems_office_id: pricing_template_data?.ems_office_id?.value || null,
            template_id: template_id || null,
        };
        createEMSBootApp(data)
            .then((res) => {
                let URLCreate = MAIN_URL + props.user.agent_url + "/" + res.data.editslug;
                window.location = URLCreate;
            })
            .catch((error) => {
                if (error.status == 422) {
                    setErrors(error.data.errors);
                }
                // scrollToTop();
            });
    };

    const scrollToTop = () => {
        if (modalContentRef.current) {
            modalContentRef.current.node
                .querySelector(".ems-boot-modal")
                .scrollTo({ top: 900, behavior: "smooth" });
        }
    };

    const modalContentRef = useRef(null);

    const sendToMerchant = () => {
        if (checkPricingValidation()) {
            return;
        }
        let data = {
            ...form_data,
            expedited_funding,
            billing_sequence,
            batch_time,
            equipment_address,
            invoice_address,
            special_instructions,
            uw_notes,
            submission_type: "send_to_merchant",
            mcc_id: pricing_template_data?.mcc_id?.value || null,
            ems_office_id: pricing_template_data?.ems_office_id?.value || null,
            template_id: template_id || null,
        };
        createEMSBootApp(data)
            .then((res) => {
                if (res.status) {
                    props.close();
                } else {
                    setErrors({ error: ["Something went wrong!"] });
                }
            })
            .catch((error) => {
                if (error.status == 422) {
                    setErrors(error.data.errors);
                }
                scrollToTop();
            });
    };

    const addQuantity = (eqp, type, slug) => {
        let copy_eqp = form_data.equipments;
        copy_eqp[type][slug].push(eqp);
        setFormData({
            ...form_data,
            equipments: copy_eqp,
        });
    };

    const removeQuantity = (eqp, type, slug) => {
        let copy_eqp = form_data.equipments;
        let removed = copy_eqp[type][slug].pop();
        setFormData({
            ...form_data,
            equipments: copy_eqp,
        });
    };

    const updateLineItemEqp = (eqp, type, slug) => {
        let copy_eqp = form_data.equipments;
        let array = copy_eqp[type][slug];
        let new_array = Array.from({ length: array.length }, () => eqp);
        copy_eqp[type][slug] = new_array;

        let purchase_type = eqp.type;
        if (purchase_type !== type) {
            let copy_eqp = _.cloneDeep(form_data.equipments);
            delete copy_eqp[type][slug];
            const purchaseType = copy_eqp[purchase_type] || {};
            let new_obj = {
                ...copy_eqp,
                [purchase_type]: {
                    ...purchaseType,
                    [slug]: new_array,
                },
            };
            eventEmitter.emit("updateEquipment", new_obj);
        } else {
            setFormData({
                ...form_data,
                equipments: copy_eqp,
            });
        }
    };

    const removeEquipment = (type, slug) => {
        let copy_eqp = _.cloneDeep(form_data.equipments);
        delete copy_eqp[type][slug];
        eventEmitter.emit("updateEquipment", copy_eqp);
        setIsDeviceExpanded(false);
    };

    const [pricing_template_data, setPricingTemplateData] = useState({});
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const handleTemplateChange = (selected) => {
        const template = all_templates.find(t => t.id == selected.value);
        setTemplateId(selected.value);
        if(template){
          setSelectedTemplate(template);
            setPricingTemplateData(template.data);
        }
    };

    useEffect(() => {
        if (
            form_data?.equipments &&
            Object.entries(form_data?.equipments).length > 0
        ) {
            let category_array = [];
            let tpp_equipments = [];
            Object.entries(form_data?.equipments).forEach(
                ([purchase_type, equipments_object]) => {
                    Object.entries(equipments_object).forEach(
                        ([eqp_slug, equipments]) => {
                            if (Array.isArray(equipments)) {
                                equipments.forEach((eqp) => {
                                    category_array.push(eqp?.category_id);
                                });
                                if(purchase_type == 'apply_tpp' && equipments.length)
                                {
                                    tpp_equipments.push(eqp_slug);
                                }
                            }
                        },
                    );
                },
            );
            setCategoriesSelected(category_array);
            setTppEquipmentsSelected(tpp_equipments);
        }
    }, [form_data]);

    const equipment_addr_ref = useRef(null);
    const invoice_addr_ref = useRef(null);

    useEffect(() => {
        if (props.user?.organization?.pricing_restricted == 1) {
            setIsEditable(false);
        } else {
            if (selectedTemplate !== null) {
                if (selectedTemplate?.is_editable == 1) {
                    setIsEditable(true);
                } else {
                    setIsEditable(false);
                }
            } else {
                setIsEditable(true);
            }
        }
    }, [props.user, selectedTemplate]);

    return (
        <ReactModal
            isOpen={isOpen}
            contentLabel="EMS Boot Modal"
            className="ems-boot-modal"
            ref={modalContentRef}
        >
            <div className="sticky-header">
                <div className="icons">
                    <button
                        className="close-modal-btn"
                        onClick={() => props.close()}
                        type="button"
                    >
                        <FaTimes />
                    </button>
                    <AddedItemCount
                        equipments={form_data.equipments}
                        setEquipmentCount={setEquipmentCount}
                    />
                </div>
            </div>
            <div
                className="header-not-sticky"
                style={
                    org_id !== PAYCHEX_ORG_ID
                        ? { justifyContent: "center" }
                        : {}
                }
            >
                <h3 className="heading-text">Direct App Creation</h3>
            </div>
            <div className="scrollable-div">
                <ContactInformation
                    form_data={form_data}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    setAddressByGoogleAPI={setAddressByGoogleAPI}
                    errors={errors}
                    setErrors={setErrors}
                    country={form_data.country}
                />
                {org_id == PAYCHEX_ORG_ID && (
                    <PaychexOptions
                        form_data={form_data}
                        setFormDataByKeyValue={setFormDataByKeyValue}
                        errors={errors}
                        setErrors={setErrors}
                        all_equipment_list={all_equipment_list}
                    />
                )}
                {org_id == EMS_ORG_ID && (
                    <MerchantReferral
                        form_data={form_data}
                        setFormDataByKeyValue={setFormDataByKeyValue}
                        errors={errors}
                        setErrors={setErrors}
                    />
                )}

               <div className="pricing-model-container mt-4r" >
                  <p className="sub-heading-lead"> Select a template</p>
                  <Select 
                    options={templateOptions} 
                    onChange={handleTemplateChange}
                    styles={{
                        menu: (base) => ({
                        ...base,
                        zIndex: 1000
                        }),
                    }}
                  />
                {
                  !isEditable && selectedTemplate != null && (
                    <p className="error-message">This template data is not editable</p>
                  )
                }
               </div>
                
                <EquipmentContext.Provider
                    value={{
                        allEquipments,
                        isDeviceExpanded,
                        setIsDeviceExpanded,
                        setErrors,
                        categories_selected,
                        setCategoriesSelected,
                        org_id,
                        tpp_equipments_selected,
                        setVarOnlyData,
                        pricingDefaults
                    }}
                >
                    <PricingModel
                        pricingModels={pricing_model_options}
                        setFormDataByKeyValue={
                            isEditable ? setFormDataByKeyValue : () => { }
                        }
                        errors={errors}
                        form_data={form_data}
                        removeError={removeError}
                        // allEquipments={allEquipments}
                        isEditable={isEditable}
                    />
                    <hr className="horizontal-rule" />

                    {Object.entries(form_data.equipments).length > 0 &&
                        equipment_count > 0 && (
                            <LineItems
                                equipments_data={form_data.equipments}
                                addQuantity={isEditable ? addQuantity : () => { }}
                                removeQuantity={isEditable ? removeQuantity : () => { }}
                                updateEquipment={isEditable ? updateLineItemEqp : () => { }}
                                removeEquipment={isEditable ? removeEquipment : () => {}}
                                allEquipments={allEquipments}
                                action={true}
                                equipmentCount={equipment_count}
                                isEditable={isEditable}
                            />
                        )}
                </EquipmentContext.Provider>
                
                {org_id == EMS_ORG_ID && (
                    <CancellationFee
                        handleToggleChange={(_e, v) => setCoverCancellation(v)}
                        handleInputChange={(e) =>
                            setReimbursementMaximum(e.target.value)
                        }
                        title="Cover cancellation fees they'll incur by switching?"
                        toggleValue={coverCancellation}
                        inputValue={reimbursementMaximum}
                        options={[
                            { key: "yes", value: "Yes" },
                            { key: "no", value: "No" },
                        ]}
                        className="expedited-funding mt-4r"
                    />
                )}

                {org_id == PAYCHEX_ORG_ID && (
                    <div className="row">
                        <div className="col-6 col-md-6 col-lg-6">
                            <SlidingButton
                                handleChange={(_e, v) =>
                                     setFormDataByKeyValue(
                                        "who_to_deploy_equipment",
                                        v,
                                    )
                                }
                                title="Who to deploy equipment?"
                                value={form_data.who_to_deploy_equipment}
                                options={[
                                    { key: "agent", value: "Agent" },
                                    { key: "processor", value: "Processor" },
                                ]}
                                className="expedited-funding mt-4r"
                            />
                        </div>
                        <div className="col-6 col-md-6 col-lg-6">
                            <SlidingButton
                                handleChange={(_e, v) =>
                                     setFormDataByKeyValue(
                                        "who_to_pay_for_equipment",
                                        v,
                                    )
                                }
                                title="Who to pay for equipment?"
                                value={form_data.who_to_pay_for_equipment}
                                options={[
                                    { key: "agent", value: "Agent" },
                                    { key: "merchant", value: "Merchant" },
                                ]}
                                className="expedited-funding mt-4r"
                            />
                        </div>
                    </div>
                )}

                <div className="row">
                    {org_id == PAYCHEX_ORG_ID && (
                        <div className="col-6 col-md-6 col-lg-6">
                            <SlidingButton
                                handleChange={(_e, v) =>
                                     setFormDataByKeyValue(
                                        "who_to_deploy_gateway",
                                        v,
                                    )
                                }
                                title="Who to deploy gateway?"
                                value={form_data.who_to_deploy_gateway}
                                options={[
                                    { key: "agent", value: "Agent" },
                                    { key: "processor", value: "Processor" },
                                ]}
                                className="expedited-funding mt-4r"
                            />
                        </div>
                    )}
                    <div
                        className={
                            org_id !== PAYCHEX_ORG_ID
                                ? "col-12 col-md-12 col-lg-12"
                                : "col-6 col-md-6 col-lg-6"
                        }
                    >
                        <SlidingButton
                            handleChange={(_e, v) =>
                                isEditable ? setExpeditedFunding(v) : () => { }
                            }
                            title="Opt merchant into expedited funding?"
                            value={expedited_funding}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            className="expedited-funding mt-4r"
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 col-md-6 col-lg-6">
                        <SlidingButton
                            handleChange={(_e, v) =>
                                isEditable ? setBillingSequence(v) : () => { }
                            }
                            title="Select the preferred billing sequence."
                            value={billing_sequence}
                            options={
                                ["dual_pricing", "surcharge"].includes(
                                    form_data.pricing_model,
                                )
                                    ? [{ key: "daily", value: "Daily" }]
                                    : [
                                        { key: "daily", value: "Daily" },
                                        { key: "monthly", value: "Monthly" },
                                    ]
                            }
                            className="billing-sequence mt-4r"
                        />
                    </div>
                    <div className="col-6 col-md-6 col-lg-6">
                        <div className="pricing-model-container mt-4r">
                            <p className="sub-heading-lead">
                                Select the desired auto batch time.
                            </p>
                            <div className="row">
                                <div className="col-12 col-md-12 col-sm-12 col-lg-12">
                                    <BatchTime
                                        onChange={(time) => {
                                            isEditable ? setBatchTime(time) : () => { }
                                            removeError("batch_time");
                                        }}
                                        errors={errors}
                                        value={batch_time}
                                    />
                                </div>
                            </div>
                        </div>
                        {errors?.batch_time &&
                            Array.isArray(errors.batch_time) &&
                            errors.batch_time.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                </div>

                <div
                    className="pricing-model-container mt-4r"
                    tabindex="-1"
                    ref={equipment_addr_ref}
                >
                    <p className="sub-heading-lead">
                        Select the address we should ship the equipment to
                    </p>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="pricing-model-buttons"
                    >
                        <button
                            type="button"
                            className={
                                equipment_address == "legal_address"
                                    ? "active"
                                    : ""
                            }
                            onClick={() => {
                                setEquipmentAddress("legal_address");
                                removeError("equipment_address");
                            }}
                            disabled={!isEditable}
                        >
                            {equipment_address == "legal_address" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            Legal Address
                        </button>
                        <button
                            type="button"
                            className={
                                equipment_address == "home_address"
                                    ? "active"
                                    : ""
                            }
                            onClick={() => {
                                setEquipmentAddress("home_address");
                                removeError("equipment_address");
                            }}
                            disabled={!isEditable}
                        >
                            {equipment_address == "home_address" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            Home Address
                        </button>
                        <button
                            type="button"
                            className={
                                equipment_address == "dba_address"
                                    ? "active"
                                    : ""
                            }
                            onClick={() => {
                                setEquipmentAddress("dba_address");
                                removeError("equipment_address");
                            }}
                            disabled={!isEditable}
                        >
                            {equipment_address == "dba_address" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            DBA Address
                        </button>
                        {org_id == PAYCHEX_ORG_ID && (
                            <button
                                type="button"
                                className={
                                    equipment_address == "agent_adress"
                                        ? "active"
                                        : ""
                                }
                                onClick={() => {
                                    setEquipmentAddress("agent_adress");
                                    removeError("equipment_address");
                                }}
                                disabled={!isEditable}
                            >
                                {equipment_address == "agent_adress" ? (
                                    <IoMdCheckmark />
                                ) : (
                                    ""
                                )}{" "}
                                Agent Address
                            </button>
                        )}
                    </Stack>
                </div>
                {errors?.equipment_address &&
                    Array.isArray(errors.equipment_address) &&
                    errors.equipment_address.map((error) => (
                        <span className="error-message">{error}</span>
                    ))}

                <div
                    className="pricing-model-container mt-4r"
                    tabindex="-1"
                    ref={invoice_addr_ref}
                >
                    <p className="sub-heading-lead">
                        Select the address we should send the invoice to
                    </p>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="pricing-model-buttons"
                    >
                        <button
                            type="button"
                            className={
                                invoice_address == "legal_address"
                                    ? "active"
                                    : ""
                            }
                            onClick={() => {
                                setInvoiceAddress("legal_address");
                                removeError("invoice_address");
                            }}
                            disabled={!isEditable}
                        >
                            {invoice_address == "legal_address" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            Legal Address
                        </button>
                        <button
                            type="button"
                            className={
                                invoice_address == "home_address"
                                    ? "active"
                                    : ""
                            }
                            onClick={() => {
                                setInvoiceAddress("home_address");
                                removeError("invoice_address");
                            }}
                            disabled={!isEditable}
                        >
                            {invoice_address == "home_address" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            Home Address
                        </button>
                        <button
                            type="button"
                            className={
                                invoice_address == "dba_address" ? "active" : ""
                            }
                            onClick={() => {
                                setInvoiceAddress("dba_address");
                                removeError("invoice_address");
                            }}
                            disabled={!isEditable}
                        >
                            {invoice_address == "dba_address" ? (
                                <IoMdCheckmark />
                            ) : (
                                ""
                            )}{" "}
                            DBA Address
                        </button>
                        {org_id == PAYCHEX_ORG_ID && (
                            <button
                                type="button"
                                className={
                                    invoice_address == "agent_adress"
                                        ? "active"
                                        : ""
                                }
                                onClick={() => {
                                    setInvoiceAddress("agent_adress");
                                    removeError("invoice_address");
                                }}
                                disabled={!isEditable}
                            >
                                {invoice_address == "agent_adress" ? (
                                    <IoMdCheckmark />
                                ) : (
                                    ""
                                )}{" "}
                                Agent Address
                            </button>
                        )}
                    </Stack>
                </div>
                {errors?.invoice_address &&
                    Array.isArray(errors.invoice_address) &&
                    errors.invoice_address.map((error) => (
                        <span className="error-message">{error}</span>
                    ))}

                <div className="pricing-model-container mt-4r">
                    <div className="row">
                        <div className="col-12 col-md-12 col-sm-12 col-lg-12">
                            <div className="floating-form textarea-field">
                                <Form.Group size="lg" controlId="dba_name">
                                    <Form.Control
                                        placeholder=""
                                        name="special_instructions"
                                        value={special_instructions}
                                        onChange={(e) =>
                                            setSpecialInstruction(
                                                e.target.value,
                                            )
                                        }
                                        className="input-control"
                                        as="textarea"
                                    />
                                    <Form.Label className="floating-label">
                                        Special Instructions
                                    </Form.Label>
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pricing-model-container mt-4r">
                    <div className="row">
                        <div className="col-12 col-md-12 col-sm-12 col-lg-12">
                            <div className="floating-form textarea-field">
                                <Form.Group size="lg" controlId="uw_notes">
                                    <Form.Control
                                        placeholder=""
                                        name="uw_notes"
                                        value={uw_notes}
                                        onChange={(e) =>
                                            setUwNotes(
                                                e.target.value,
                                            )
                                        }
                                        className="input-control"
                                        as="textarea"
                                    />
                                    <Form.Label className="floating-label">
                                        Underwriting Notes
                                    </Form.Label>
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                </div>
                {Object.entries(errors).length > 0 ? (
                    <>
                        <p className="error-message mt-2r all-errors">
                            Please fix the following fields:
                            <br />
                            <span className="bold-error">
                                {Object.entries(errors).map(([key, error], index) => (
                                    <span key={index}>
                                        {key.includes('.') ? key.split('.')[0].replace(/_/g, " ") : key.replace(/_/g, " ")}: {error}
                                        <br />
                                    </span>
                                ))}
                            </span>
                        </p>
                    </>
                ) : (
                    <></>
                )}
                {errors?.error &&
                    Array.isArray(errors.error) &&
                    errors.error.map((error) => (
                        <span className="error-message">{error}</span>
                    ))}                


                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="submit-section"
                >
                    <button
                        type="button"
                        className="send-to-merchant"
                        onClick={sendToMerchant}
                        disabled={Object.entries(errors).length > 0}
                    >
                        Send to Merchant
                    </button>
                    <button
                        type="button"
                        className="generate-app"
                        onClick={submitForm}
                        disabled={Object.entries(errors).length > 0}
                    >
                        Generate App ➜
                    </button>
                </Stack>
            </div>
        </ReactModal>
    );
}

export default EMSBootModal;
